/**
 * CommentForm - Popup form for adding new comments or replies
 */
import React, { useState, useRef, useEffect } from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faTimes, faComment, faPaperPlane } from "@fortawesome/free-solid-svg-icons"
import { useDraggablePanel } from "hooks/core/useDraggablePanel"

interface CommentFormProps {
  isVisible: boolean
  selectedText: string
  position?: { x: number; y: number } | null
  onSubmit: (content: string) => void
  onCancel: () => void
  /** If provided, this is a reply form */
  isReply?: boolean
  /** Placeholder text */
  placeholder?: string
}

const FORM_WIDTH = 320
const FORM_HEIGHT_ESTIMATE = 280 // Approximate height including selected text

export const CommentForm: React.FC<CommentFormProps> = ({
  isVisible,
  selectedText,
  position,
  onSubmit,
  onCancel,
  isReply = false,
  placeholder = "Add a comment...",
}) => {
  const [content, setContent] = useState("")
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const formRef = useRef<HTMLDivElement>(null)
  const [initialPosition, setInitialPosition] = useState({ x: 0, y: 0 })
  const { position: dragPosition, isDragging, handleHeaderMouseDown } = useDraggablePanel(initialPosition)

  // Calculate smart initial position when form becomes visible
  useEffect(() => {
    if (isVisible && position) {
      const viewportWidth = window.innerWidth
      const viewportHeight = window.innerHeight

      // Start with the provided position
      let x = position.x
      let y = position.y

      // Ensure form doesn't go off the right edge
      if (x + FORM_WIDTH > viewportWidth - 20) {
        x = viewportWidth - FORM_WIDTH - 20
      }

      // Ensure form doesn't go off the left edge
      if (x < 20) {
        x = 20
      }

      // If form would be cut off at bottom, position it above the selection
      if (y + FORM_HEIGHT_ESTIMATE > viewportHeight - 20) {
        // Position above the click point instead
        y = Math.max(20, position.y - FORM_HEIGHT_ESTIMATE - 20)
      }

      // Ensure form doesn't go off the top edge
      if (y < 20) {
        y = 20
      }

      setInitialPosition({ x, y })
    } else if (isVisible && !position) {
      // Center in viewport if no position provided
      setInitialPosition({
        x: (window.innerWidth - FORM_WIDTH) / 2,
        y: (window.innerHeight - FORM_HEIGHT_ESTIMATE) / 2,
      })
    }
  }, [isVisible, position])

  // Focus textarea when form becomes visible
  useEffect(() => {
    if (isVisible && textareaRef.current) {
      // Small delay to ensure position is set first
      setTimeout(() => textareaRef.current?.focus(), 50)
    }
  }, [isVisible])

  // Reset content and position when form closes
  useEffect(() => {
    if (!isVisible) {
      setContent("")
      setInitialPosition({ x: 0, y: 0 })
    }
  }, [isVisible])

  // Handle Escape key to close
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isVisible) {
        onCancel()
      }
    }

    document.addEventListener("keydown", handleKeyDown)
    return () => document.removeEventListener("keydown", handleKeyDown)
  }, [isVisible, onCancel])

  // Handle click outside to close (but not while dragging)
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        formRef.current &&
        !formRef.current.contains(e.target as Node) &&
        isVisible &&
        !isDragging
      ) {
        onCancel()
      }
    }

    // Delay adding listener to prevent immediate close
    const timeout = setTimeout(() => {
      document.addEventListener("mousedown", handleClickOutside)
    }, 100)

    return () => {
      clearTimeout(timeout)
      document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [isVisible, onCancel, isDragging])

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (content.trim()) {
      onSubmit(content.trim())
      setContent("")
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    // Submit on Ctrl+Enter or Cmd+Enter
    if ((e.ctrlKey || e.metaKey) && e.key === "Enter") {
      e.preventDefault()
      handleSubmit(e)
    }
  }

  if (!isVisible) return null

  // Use drag position if dragging has occurred, otherwise use initial position
  const finalX = dragPosition.x !== 0 || dragPosition.y !== 0 ? dragPosition.x : initialPosition.x
  const finalY = dragPosition.x !== 0 || dragPosition.y !== 0 ? dragPosition.y : initialPosition.y

  return (
    <div
      ref={formRef}
      className="bg-white rounded-lg shadow-2xl z-[1001] pointer-events-auto"
      style={{
        position: "fixed",
        left: `${finalX}px`,
        top: `${finalY}px`,
        width: `${FORM_WIDTH}px`,
        border: "1px solid #D1D5DB",
        cursor: isDragging ? "grabbing" : undefined,
      }}
    >
      {/* Header - Draggable */}
      <div
        className="px-4 py-3 rounded-t-lg flex items-center justify-between cursor-grab active:cursor-grabbing"
        style={{ backgroundColor: "#36C3ED" }}
        onMouseDown={handleHeaderMouseDown}
      >
        <div className="flex items-center gap-2 text-white">
          <FontAwesomeIcon icon={faComment} />
          <span className="font-semibold">
            {isReply ? "Add Reply" : "Add Comment"}
          </span>
        </div>
        <button
          onClick={onCancel}
          className="text-white hover:text-gray-200 transition-colors"
          aria-label="Close comment form"
        >
          <FontAwesomeIcon icon={faTimes} />
        </button>
      </div>

      {/* Selected Text Preview (only for new comments) */}
      {!isReply && selectedText && (
        <div className="px-4 py-2 bg-yellow-50 border-b border-yellow-200">
          <div className="text-xs text-gray-500 mb-1">Selected text:</div>
          <div className="text-sm text-gray-700 italic line-clamp-2">
            "{selectedText}"
          </div>
        </div>
      )}

      {/* Form */}
      <form onSubmit={handleSubmit} className="p-4">
        <textarea
          ref={textareaRef}
          value={content}
          onChange={(e) => setContent(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          className="w-full p-3 border border-gray-300 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-cyan-400 focus:border-transparent"
          rows={3}
          maxLength={2000}
        />

        {/* Character count */}
        <div className="flex justify-between items-center mt-2">
          <span className="text-xs text-gray-400">
            {content.length}/2000 characters
          </span>
          <span className="text-xs text-gray-400">Ctrl+Enter to submit</span>
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-2 mt-3">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={!content.trim()}
            className="px-4 py-2 text-sm text-white rounded-lg transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            style={{
              backgroundColor: content.trim() ? "#36C3ED" : "#9CA3AF",
            }}
          >
            <FontAwesomeIcon icon={faPaperPlane} />
            {isReply ? "Reply" : "Comment"}
          </button>
        </div>
      </form>
    </div>
  )
}

export default CommentForm
